import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, ref } from 'vue';
import { searchRolePermissions } from '@infuse/api/iam/role';
import { Permission } from '@infuse/api/iam/permission';
import { Pageable } from '@infuse/api/common';
import { searchOrganisationRolePermissions } from '@infuse/api/iam/organisation';
import { searchTenantRolePermissions } from '@infuse/api/iam/tenant';

export const useRolePermissions = () => {
  const fetchRoleId = ref<string | undefined>(undefined);
  const fetchSubscriptionId = ref<string | undefined>(undefined);
  const levelType = ref<string | undefined>(undefined);
  const levelId = ref<string | undefined>(undefined);
  const pageSize = 99999;

  const query = useInfiniteQuery<Pageable<Permission>>({
    initialPageParam: 0,
    queryKey: ['role-permissions', fetchRoleId, levelType, levelId],
    enabled: () => !!fetchRoleId.value && (!levelType.value || !!levelId.value),
    queryFn: async ({ pageParam }) => {
      if (levelType.value && levelId.value && levelType.value === 'organisation') {
        return await searchOrganisationRolePermissions(
          {
            size: pageSize,
            page: Number.parseInt(`${pageParam}`),
            sort: [{ key: 'name' }]
          },
          undefined,
          { roleId: fetchRoleId.value ?? '', id: levelId.value }
        );
      }

      if (levelType.value && levelId.value && levelType.value === 'tenant') {
        return await searchTenantRolePermissions(
          {
            size: pageSize,
            page: Number.parseInt(`${pageParam}`),
            sort: [{ key: 'name' }]
          },
          undefined,
          { roleId: fetchRoleId.value ?? '', id: levelId.value }
        );
      }

      return await searchRolePermissions(
        {
          size: pageSize,
          page: Number.parseInt(`${pageParam}`),
          sort: [{ key: 'name' }]
        },
        undefined,
        { roleId: fetchRoleId.value ?? '' }
      );
    },
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.pageable.pageNumber + 1)
  });

  const permissions = computed(() => query.data.value?.pages.flatMap((page) => page.content) || []);

  return {
    ...query,
    fetchRoleId,
    fetchSubscriptionId,
    permissions,
    levelType,
    levelId
  };
};
